import { useCallback, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useInstallationCamera } from "./installation/useInstallationCamera";
import {
  getCameraForSide,
  isInstallationSide,
  isPhotoSideEnabled,
  INSTALLATION_DETAIL_STALE_KEY,
  INSTALLATION_LIST_STALE_KEY,
} from "./installation/installationConfigUtils";
import {
  savePhotoSideConfig,
  updateCameraContainers,
} from "./installation/installationApi";
import InstallationSideHubSection from "./installation/InstallationSideHubSection";
import InstallationSubpageLayout from "./installation/InstallationSubpageLayout";
import { useInstallationSideLabels } from "./installation/installationSideUtils";
import { useInstallationImageRotation } from "./installation/useInstallationImageRotation";
import { formatRotationHubSubtitle } from "./installation/installationRotationUtils";

const AppInstallationSideHub = ({ setCookies, saveUser }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id, side } = useParams();
  const { getSideImageTitle } = useInstallationSideLabels();
  const [saving, setSaving] = useState(false);

  const {
    loading,
    leftCamera,
    rightCamera,
    configData,
    setConfigData,
    cycleEntries,
    api,
    token,
    reload,
  } = useInstallationCamera(id);

  const validSide = isInstallationSide(side) ? side : "left";
  const camera = getCameraForSide(leftCamera, rightCamera, validSide);

  const { appliedAngle, appliedMatched, targetAngle, monitoringLoading } =
    useInstallationImageRotation({
      routeCameraId: id,
      side: validSide,
      cycleEntries,
      camera,
    });

  const rotationSubtitle = monitoringLoading
    ? t("installation_loading")
    : formatRotationHubSubtitle(t, appliedAngle, targetAngle, appliedMatched);

  const saveContainers = useCallback(
    async (containerObjectIdList) => {
      if (!camera?._id) {
        return false;
      }

      setSaving(true);
      try {
        await updateCameraContainers(
          api,
          token,
          camera._id,
          validSide,
          containerObjectIdList,
        );
        sessionStorage.setItem(INSTALLATION_LIST_STALE_KEY, "1");
        sessionStorage.setItem(INSTALLATION_DETAIL_STALE_KEY, "1");
        toast.success(t("installation_saved"));
        await reload({ silent: true });
        return true;
      } catch {
        toast.error(t("installation_save_failed"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [api, camera?._id, reload, t, token, validSide],
  );

  const savePhoto = useCallback(
    async (photoSide, enabled) => {
      if (isPhotoSideEnabled(configData, photoSide) === enabled) {
        return true;
      }

      setSaving(true);
      try {
        const { output } = await savePhotoSideConfig(
          api,
          token,
          id,
          configData,
          photoSide,
          enabled,
        );
        setConfigData(output);
        sessionStorage.setItem(INSTALLATION_LIST_STALE_KEY, "1");
        sessionStorage.setItem(INSTALLATION_DETAIL_STALE_KEY, "1");
        toast.success(t("installation_saved"));
        await reload({ silent: true });
        return true;
      } catch {
        toast.error(t("installation_save_failed"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [api, token, id, configData, setConfigData, t, reload],
  );

  if (!isInstallationSide(side)) {
    return <Redirect to={`/app/installationdetails/${id}`} />;
  }

  const pageTitle = getSideImageTitle(validSide);

  return (
    <InstallationSubpageLayout
      pageTitle={pageTitle}
      onBack={() => history.goBack()}
      setCookies={setCookies}
      saveUser={saveUser}
    >
      {loading && !camera && (
        <div className="installation-detail-state">
          <div className="installation-detail-spinner" aria-hidden />
          <p>{t("installation_loading")}</p>
        </div>
      )}
      {camera && (
        <InstallationSideHubSection
          routeCameraId={id}
          side={validSide}
          camera={camera}
          corporationId={leftCamera?.corporationId}
          addressId={leftCamera?.addressId}
          api={api}
          token={token}
          configData={configData}
          onPhotoSave={savePhoto}
          onContainersSave={saveContainers}
          rotationSubtitle={rotationSubtitle}
          disabled={saving}
          saving={saving}
        />
      )}
    </InstallationSubpageLayout>
  );
};

export default AppInstallationSideHub;
